/**
 * УТП-логіка дефолтів договору у документах продажів/закупівель (П1, 2026-07-23):
 *
 * 1. `useMainContractAutoSelect` — при виборі контрагента автоматично
 *    підставляється його ОСНОВНИЙ договір (is_main, 0373) або єдиний наявний.
 *    Вибір користувача не переграється (guard по contractor_id).
 *
 * 2. `useContractPriceLookup` — ціна номенклатури за ТИПОМ ЦІН договору
 *    (ref.contracts.price_type_id, 0372): як в УТП при підборі номенклатури
 *    ціна підставляється з прайсу типу цін договору. Без типу цін — null
 *    (ціна лишається ручною).
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { refs, type Contract, type NomenclaturePriceRow } from '@/api/refs';

export function useMainContractAutoSelect(opts: {
  enabled: boolean;
  contractorId: string;
  contractId: string;
  contracts: Contract[] | undefined;
  onSelect: (contractId: string) => void;
}) {
  const { enabled, contractorId, contractId, contracts, onSelect } = opts;
  // Один автовибір на контрагента — щоб не переграти ручне «без договору».
  const appliedFor = useRef<string>('');
  useEffect(() => {
    if (!enabled || !contractorId || contractId || !contracts?.length) return;
    if (appliedFor.current === contractorId) return;
    const main = contracts.find((c) => c.is_main)
      ?? (contracts.length === 1 ? contracts[0] : undefined);
    if (main) {
      appliedFor.current = contractorId;
      onSelect(main.id);
    }
  }, [enabled, contractorId, contractId, contracts, onSelect]);
}

export function useContractPriceLookup(
  contracts: Contract[] | undefined,
  contractId: string,
  /** ч.922: організація і дата документа — щоб закупівельна ціна (і типи
   *  «закупівельна + націнка») рахувались з надходжень цієї організації. */
  organizationId?: string,
  onDate?: string,
) {
  const qc = useQueryClient();
  const priceTypeId =
    contracts?.find((c) => c.id === contractId)?.price_type_id ?? null;

  /** Ціна номенклатури за типом цін договору; null — не підставляти. */
  async function priceFor(nomenclatureId: string): Promise<string | null> {
    if (!priceTypeId || !nomenclatureId) return null;
    try {
      const rows = await qc.fetchQuery<NomenclaturePriceRow[]>({
        queryKey: ['nomenclature-prices', nomenclatureId, organizationId ?? '', (onDate ?? '').slice(0, 10)],
        queryFn: () => refs.nomenclaturePrices.listByNomenclature(nomenclatureId,
          organizationId ? { organization_id: organizationId, on_date: onDate } : undefined),
        staleTime: 60_000,
      });
      const row = rows.find((r) => r.price_type_id === priceTypeId);
      const p = row?.computed_price ?? row?.price;
      return p != null && Number(p) > 0 ? String(p) : null;
    } catch {
      return null; // ціни — допоміжний сервіс, не блокуємо ввід рядка
    }
  }

  return { priceTypeId, priceFor };
}
